import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ArticleLabelIcon from "../../assets/icons/article-label.svg?react";
import { PaperCard } from "../ui/PaperCard";
import { renderDateRelative } from "../../domains/app/utils/renderDate";
import { usePageLinks } from "../../domains/app/hooks/usePageLinks";
import {
  assistantConversationListTitle,
  firstMessageText,
} from "../../domains/gpt/transcript";
import type { Api } from "../../services/api";

// The latest conversation of the engine that answers new questions.
type LatestConversation =
  | {
      engine: "legacy";
      doc: Api["get"]["/webapp/private/conversations"]["output"]["body"]["docs"][number];
    }
  | {
      engine: "assistant";
      doc: Api["get"]["/assistant/conversations"]["output"]["body"]["docs"][number];
    };
type ChatWithPaperSession =
  Api["get"]["/webapp/private/chat-with-paper/sessions"]["output"]["body"]["docs"][number];

function conversationTeaser(conversation: LatestConversation) {
  switch (conversation.engine) {
    case "legacy":
      return {
        id: conversation.doc.id,
        title:
          conversation.doc.title === null
            ? firstMessageText(conversation.doc.messages)
            : conversation.doc.title,
        date: conversation.doc.createdAt,
      };
    case "assistant":
      return {
        id: conversation.doc.id,
        title: assistantConversationListTitle(conversation.doc),
        date: conversation.doc.sortDate,
      };
    default:
      throw new Error(conversation satisfies never);
  }
}

/**
 * The reader's most recent AI chat, whichever surface it happened on: a
 * JuisciGPT conversation or a Chat With Paper session.
 *
 * The two live behind two endpoints, so the profile asks each for its latest
 * and keeps the newer one. Each is dated by the key its list is sorted by: a
 * legacy conversation by its creation, an assistant list row by its `sortDate`,
 * and a session by its last update.
 */
export function pickLatestAiChat(
  latest: LatestConversation | null,
  session: ChatWithPaperSession | null
) {
  const conversation = latest === null ? null : conversationTeaser(latest);
  if (conversation !== null && session !== null) {
    return new Date(conversation.date).getTime() >=
      new Date(session.updatedAt).getTime()
      ? ({ type: "gpt", data: conversation } as const)
      : ({ type: "cwyop", data: session } as const);
  }
  if (conversation !== null)
    return { type: "gpt", data: conversation } as const;
  if (session !== null) return { type: "cwyop", data: session } as const;
  return null;
}

function formatJournalInfo(
  journalName: string | null,
  publicationDate: string | null
): string {
  const year =
    publicationDate === null
      ? null
      : new Date(publicationDate).getUTCFullYear().toString();
  if (journalName !== null && year !== null) return `${journalName} · ${year}`;
  if (journalName !== null) return journalName;
  if (year !== null) return year;
  return "";
}

/**
 * The profile's teaser for that chat, linking back into it. Shared by the
 * mobile and the desktop profile so the two never disagree on what the latest
 * chat is or how it reads.
 */
export function LatestAiChatCard({
  chat,
}: {
  chat: NonNullable<ReturnType<typeof pickLatestAiChat>>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { gptConversationPageLink, chatWithPaperConversationLink } =
    usePageLinks(null);

  switch (chat.type) {
    case "gpt":
      return (
        <Link
          to={gptConversationPageLink(chat.data.id)}
          className="block no-underline"
        >
          <PaperCard
            variant="gpt"
            label="JuisciGPT"
            dateLabel={renderDateRelative(new Date(chat.data.date))}
            title={chat.data.title}
            subtitle={t("Multi-turn conversation")}
          />
        </Link>
      );
    case "cwyop":
      return (
        <Link
          to={chatWithPaperConversationLink(
            chat.data.articleSlug,
            chat.data.articleId
          )}
          className="block no-underline"
        >
          <PaperCard
            variant="cwyop"
            label={t("Chat With Paper")}
            dateLabel={renderDateRelative(new Date(chat.data.updatedAt))}
            icon={
              <ArticleLabelIcon className="size-3.5 shrink-0 [&_path]:fill-ds-mint-800" />
            }
            // The teaser carries the conversation and its paper's title, not
            // the article's authorship.
            author={null}
            title={chat.data.articleTitle}
            subtitle={formatJournalInfo(
              chat.data.journalName,
              chat.data.publicationDate
            )}
            quote={
              chat.data.lastQuestion === null ? "" : chat.data.lastQuestion
            }
          />
        </Link>
      );
    default:
      throw new Error(chat satisfies never);
  }
}

/**
 * The same teaser on the desktop profile (Figma "05 - Profil", "Last chat with
 * JuisciGPT"): one bordered card, the surface and the date on top, then the
 * title and what kind of chat it was.
 */
export function DesktopLatestAiChatCard({
  chat,
}: {
  chat: NonNullable<ReturnType<typeof pickLatestAiChat>>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { gptConversationPageLink, chatWithPaperConversationLink } =
    usePageLinks(null);

  const teaser = (() => {
    switch (chat.type) {
      case "gpt":
        return {
          to: gptConversationPageLink(chat.data.id),
          source: "JuisciGPT",
          date: chat.data.date,
          title: chat.data.title,
          kind: t("Multi-turn conversation"),
        };
      case "cwyop":
        return {
          to: chatWithPaperConversationLink(
            chat.data.articleSlug,
            chat.data.articleId
          ),
          source: t("Chat With Paper"),
          date: chat.data.updatedAt,
          title: chat.data.articleTitle,
          kind: formatJournalInfo(
            chat.data.journalName,
            chat.data.publicationDate
          ),
        };
      default:
        throw new Error(chat satisfies never);
    }
  })();

  return (
    <Link
      to={teaser.to}
      className="flex flex-col gap-1.5 rounded-xl border border-ds-slate-100 px-5 py-4 no-underline transition-colors hover:bg-ds-slate-50"
    >
      <div className="flex items-center justify-between gap-3 text-desktop-meta text-ds-slate-700">
        <span className="font-semibold">{teaser.source}</span>
        <span className="shrink-0">
          {renderDateRelative(new Date(teaser.date))}
        </span>
      </div>
      <p className="m-0 line-clamp-2 text-desktop-card-title font-semibold text-ds-black">
        {teaser.title}
      </p>
      {teaser.kind === "" ? null : (
        <p className="m-0 text-desktop-meta text-ds-slate-700">{teaser.kind}</p>
      )}
    </Link>
  );
}
